import { Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts'

import { rangeInclusive } from '../../tools/range'
import {
    getGraphPrimaryColor,
    getGraphStrokeColor,
    getGraphSecondaryColor,
    GRAPH_AXIS_BYTE_RANGE,
    GRAPH_BORDER_STYLE,
    GRAPH_LINE_STYLE,
    GRAPH_HALF_SIZE,
} from './graph-constants'
import Vector2 from '../../models/Vector2'

interface WaveGraphProps {
    primaryCoordinates?: Vector2[]
    primaryTrigger?: Vector2[]
    secondaryCoordinates?: Vector2[]
    secondaryTrigger?: Vector2[]
    height: number
    width: number
    strokeWidth?: number
    showAxes?: boolean
    showTooltip?: boolean
    onClick?: () => void
    animate?: boolean
}
export default function WaveGraph({
    primaryCoordinates = [],
    primaryTrigger,
    secondaryCoordinates = [],
    secondaryTrigger,
    height,
    width,
    strokeWidth = 1,
    onClick,
    showAxes = false,
    showTooltip = false,
    animate = true,
}: WaveGraphProps) {
    if (!(primaryCoordinates.length || secondaryCoordinates.length)) return undefined

    const hasSecondaryGraph = !!secondaryCoordinates?.length
    const allCoordinates = (
        hasSecondaryGraph ? [...primaryCoordinates, ...secondaryCoordinates] : primaryCoordinates
    ).filter((v) => !!v)

    const min = Math.ceil(allCoordinates.reduce((v, { x }) => (x < v ? x : v), Infinity))
    const max = Math.floor(allCoordinates.reduce((v, { x }) => (x > v ? x : v), 0))

    const xRange = rangeInclusive(min, max)
    const yRange = GRAPH_AXIS_BYTE_RANGE

    const strokeColor = getGraphStrokeColor()
    const primaryColor = getGraphPrimaryColor()
    const secondaryColor = getGraphSecondaryColor()

    return (
        <LineChart
            onClick={onClick}
            className="graph"
            style={{ cursor: onClick ? 'pointer' : undefined }}
            height={height}
            width={width}
            data={primaryCoordinates}
        >
            {showTooltip && (
                <Tooltip
                    itemStyle={{ display: 'none' }}
                    isAnimationActive={false}
                    labelFormatter={(label) => <WaveGraphTooltipFormat label={label} />}
                    cursor={GRAPH_BORDER_STYLE}
                />
            )}
            <ReferenceLine
                ifOverflow="extendDomain"
                stroke={GRAPH_LINE_STYLE.stroke}
                segment={[
                    {
                        x: allCoordinates[0].x - 100,
                        y: GRAPH_HALF_SIZE,
                    },
                    {
                        x: allCoordinates[allCoordinates.length - 1].x + 100,
                        y: GRAPH_HALF_SIZE,
                    },
                ]}
            />
            {hasSecondaryGraph && (
                <Line
                    data={secondaryCoordinates}
                    dataKey="y"
                    type="monotone"
                    stroke={secondaryColor}
                    strokeWidth={strokeWidth}
                    dot={false}
                    isAnimationActive={animate}
                />
            )}
            <Line
                dataKey="y"
                type="monotone"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                dot={false}
                isAnimationActive={animate}
            />
            {hasSecondaryGraph &&
                createArrowSegments(secondaryTrigger).map((segment, i) => (
                    <ReferenceLine
                        key={`reference-${i}`}
                        stroke={secondaryColor}
                        strokeWidth={2}
                        segment={segment}
                        ifOverflow="extendDomain"
                    />
                ))}
            {createArrowSegments(primaryTrigger).map((segment, i) => (
                <ReferenceLine
                    key={i}
                    stroke={primaryColor}
                    strokeWidth={2}
                    segment={segment}
                    ifOverflow="extendDomain"
                />
            ))}
            {showAxes && (
                <XAxis
                    allowDecimals
                    allowDataOverflow
                    type="number"
                    dataKey="x"
                    tickSize={0}
                    tickMargin={12}
                    ticks={xRange}
                    tickLine={GRAPH_LINE_STYLE}
                    axisLine={GRAPH_LINE_STYLE}
                    domain={[
                        (dataMin: number) => Math.floor(dataMin),
                        (dataMax: number) => Math.ceil(dataMax),
                    ]}
                />
            )}
            {showAxes && (
                <YAxis
                    hide
                    tick={false}
                    allowDecimals
                    allowDataOverflow
                    type="number"
                    dataKey="y"
                    ticks={yRange}
                    tickLine={GRAPH_LINE_STYLE}
                    axisLine={GRAPH_LINE_STYLE}
                    domain={['dataMin', 'dataMax']}
                />
            )}
        </LineChart>
    )
}

interface WaveGraphTooltipFormat {
    label: string
}
const WaveGraphTooltipFormat = ({ label }: WaveGraphTooltipFormat) => (
    <span className="graph__tooltip-format">
        x: <span className="graph__tooltip-format-value">{parseFloat(label).toFixed(5)}</span>
    </span>
)

const createArrowSegments = (
    trigger?: Vector2[],
    length: number = 2,
    flareOut: number = 2.5,
    flareBack: number = 0.1
) => {
    if (!(trigger && trigger[0])) return []

    const startPoint = trigger[1]
    const endPoint = trigger[0]
    const { x, y } = endPoint

    const start = startPoint?.x ?? x - length
    const end = x
    const edge = x - flareBack
    const top = y + flareOut
    const bottom = y - flareOut

    const butt = { x: start, y }
    const tip = { x: end, y }
    const topEdge = { x: edge, y: top }
    const bottomEdge = { x: edge, y: bottom }

    return [
        [butt, tip],
        [tip, topEdge],
        [tip, bottomEdge],
        // [topEdge, bottomEdge],
    ]
}
